import { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle2, Clock, AlertTriangle, MessageSquare, FileEdit, HelpCircle, Bug, Mail, ListTodo, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { MessageCenter } from './MessageCenter';
import { publicAnonKey } from '../utils/supabase/info';
import { addInboxItemToTodos, contactDraft, loadInboxItems, setInboxItemStatus } from '../utils/queueInbox';
import { toast } from 'sonner';

interface QueueItem {
  id: string;
  type: 'request' | 'change_order' | 'approval' | 'question' | 'issue';
  companyId: string;
  companyName: string;
  spaceId?: string;
  spaceName?: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'waiting_consultant' | 'waiting_client' | 'in_progress' | 'resolved';
  createdBy: string;
  createdByName: string;
  createdAt: string;
  dueDate?: string;
}

interface QueueItemsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string;
  projectId: string;
  /** The signed-in consultant (RESTORE_PLAN R1.6: the to-do goes on their list; the Message Center replies as them) */
  userId?: string;
  userName?: string;
  userEmail?: string;
  userRole?: string;
}

export function QueueItemsPanel({ isOpen, onClose, accessToken, projectId, userId = '', userName = '', userEmail = '', userRole = 'consultant' }: QueueItemsPanelProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'waiting_consultant' | 'in_progress'>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showMessageCenter, setShowMessageCenter] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchQueueItems();
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const fetchQueueItems = async () => {
    setLoading(true);
    try {
      // Waiting on the consultant or in progress, for the caller's own firm (statuses now save)
      setItems(await loadInboxItems(accessToken));
    } catch (error) {
      console.error('Error fetching queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: QueueItem['type']) => {
    switch (type) {
      case 'approval':
        return <CheckCircle2 className="size-5 text-blue-600" />;
      case 'change_order':
        return <FileEdit className="size-5 text-purple-600" />;
      case 'question':
        return <HelpCircle className="size-5 text-orange-600" />;
      case 'issue':
        return <Bug className="size-5 text-red-600" />;
      case 'request':
        return <MessageSquare className="size-5 text-green-600" />;
    }
  };

  const getPriorityColor = (priority: QueueItem['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1d ago';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredItems = items.filter(item => {
    if (filterStatus === 'all') return item.status !== 'resolved';
    return item.status === filterStatus;
  });

  const handleMarkInProgress = async () => {
    if (!selectedItem) return;
    setActionLoading('in_progress');
    try {
      const saved = await setInboxItemStatus(selectedItem.id, 'in_progress', accessToken);
      const next = { ...selectedItem, ...saved, status: 'in_progress' as const };
      setItems(items.map(item => (item.id === selectedItem.id ? next : item)));
      setSelectedItem(next);
      toast.success(`"${selectedItem.title}" marked as in progress`);
    } catch (error) {
      console.error('Error marking as in progress:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  // Reply in the app (the old card's Message Center), not the email program
  const handleContactClient = () => {
    if (!selectedItem) return;
    setShowMessageCenter(true);
  };

  const handleMarkResolved = async () => {
    if (!selectedItem) return;
    setActionLoading('resolved');
    try {
      await setInboxItemStatus(selectedItem.id, 'resolved', accessToken);
      setItems(items.map(item => (item.id === selectedItem.id ? { ...item, status: 'resolved' as const } : item)));
      setSelectedItem(null);
      toast.success(`"${selectedItem.title}" marked as resolved`);
    } catch (error) {
      console.error('Error marking as resolved:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToTodoList = async () => {
    if (!selectedItem) return;
    if (!userId) {
      toast.error('Sign in again to add to your to-do list');
      return;
    }
    setActionLoading('todo');
    try {
      const { existing } = await addInboxItemToTodos(selectedItem, userId, accessToken);
      toast.success(existing ? `"${selectedItem.title}" is already on your to-do list` : `"${selectedItem.title}" added to your to-do list`);
    } catch (error) {
      console.error('Error adding to to-do list:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add to to-do list');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <AlertCircle className="size-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold">Review Queue</h2>
              <p className="text-sm text-muted-foreground">
                Items requiring your attention
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>

        {/* Filters - Only show when viewing list */}
        {!selectedItem && (
          <div className="px-6 pt-4">
            <Tabs value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
              <TabsList>
                <TabsTrigger value="all">
                  All ({items.filter(i => i.status !== 'resolved').length})
                </TabsTrigger>
                <TabsTrigger value="waiting_consultant">
                  Waiting ({items.filter(i => i.status === 'waiting_consultant').length})
                </TabsTrigger>
                <TabsTrigger value="in_progress">
                  In Progress ({items.filter(i => i.status === 'in_progress').length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedItem ? (
            // Detail view
            <div className="space-y-6">{/* Removed h-full overflow-y-auto since parent handles scroll */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItem(null)}
                className="mb-4"
              >
                ← Back to list
              </Button>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      {getTypeIcon(selectedItem.type)}
                      <div>
                        <h3 className="text-xl font-semibold mb-1">{selectedItem.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{selectedItem.companyName}</span>
                          <span>•</span>
                          <span>From {selectedItem.createdByName}</span>
                          <span>•</span>
                          <span>{formatTime(selectedItem.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={getPriorityColor(selectedItem.priority)}>
                      {selectedItem.priority}
                    </Badge>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm">{selectedItem.description}</p>
                  </div>

                  {selectedItem.dueDate && (
                    <div className="flex items-center gap-2 text-sm text-orange-600 mb-4">
                      <Clock className="size-4" />
                      <span>Due {new Date(selectedItem.dueDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-semibold mb-3">Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleMarkInProgress}
                      disabled={actionLoading === 'in_progress' || selectedItem.status === 'in_progress'}
                    >
                      {actionLoading === 'in_progress' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <Clock className="size-4 mr-2" />
                      )}
                      Mark as In Progress
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleContactClient}
                      disabled={!!actionLoading}
                    >
                      <Mail className="size-4 mr-2" />
                      Contact Client
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleMarkResolved}
                      disabled={actionLoading === 'resolved'}
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                    >
                      {actionLoading === 'resolved' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="size-4 mr-2" />
                      )}
                      Mark as Resolved
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleAddToTodoList}
                      disabled={actionLoading === 'todo'}
                    >
                      {actionLoading === 'todo' ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <ListTodo className="size-4 mr-2" />
                      )}
                      Add to To-Do List
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-semibold mb-3">Activity</h4>
                  <p className="text-sm text-muted-foreground">No activity yet</p>
                </div>
              </div>
            </div>
          ) : (
            // List view
            <div className="space-y-3">
              {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <CheckCircle2 className="size-12 text-green-600 mb-3" />
                  <p className="font-medium">All caught up</p>
                  <p className="text-sm text-muted-foreground">No items need your attention right now</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="flex items-start gap-3">
                      {getTypeIcon(item.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-medium">{item.title}</h4>
                          <Badge className={`${getPriorityColor(item.priority)} whitespace-nowrap text-xs`}>
                            {item.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-medium">{item.companyName}</span>
                          <span>•</span>
                          <span>{item.createdByName}</span>
                          <span>•</span>
                          <span>{formatTime(item.createdAt)}</span>
                          {item.dueDate && (
                            <>
                              <span>•</span>
                              <span className="text-orange-600 flex items-center gap-1">
                                <Clock className="size-3" />
                                Due {new Date(item.dueDate).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Client: the in-app Message Center (its own dialog), the reply drafted (RESTORE_PLAN R1.6) */}
      {showMessageCenter && selectedItem && (
        <MessageCenter
          userId={userId}
          userName={userName || userEmail.split('@')[0] || 'Consultant'}
          userEmail={userEmail}
          userRole={userRole}
          companyId={selectedItem.companyId}
          projectId={projectId}
          publicAnonKey={publicAnonKey}
          accessToken={accessToken}
          onClose={() => setShowMessageCenter(false)}
          initialComposing={true}
          initialSubject={contactDraft(selectedItem).subject}
          initialContent={contactDraft(selectedItem).content}
        />
      )}
    </div>
  );
}
